// Shared utilities for the friendly-pro chart family.
// Centralizes color palette, formatters, and the auto-insight rules
// so every chart speaks the same visual language.

export const PRECIO_OFICIAL = 7.50;

export const PALETTE = {
  navy:      '#1c2136',
  green:     '#17c671',
  blue:      '#2962ff',
  yellow:    '#f5a623',
  red:       '#f53d6b',
  midGray:   '#6b7285',
  lightGray: '#9ba5bc',
  border:    '#e8eaf0',
  softBg:    '#f8f9fc',
  white:     '#ffffff',
};

export const SPANISH_MONTHS_SHORT = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
export const SPANISH_MONTHS_LONG  = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

export const fmtCurrency = (n, dec = 2) => `$${parseFloat(n).toFixed(dec)}`;
export const fmtDollars  = (n) => `$${Math.abs(parseFloat(n)).toFixed(2)}`;
export const fmtPct      = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
export const titleCase   = (s) => (s ? s[0].toUpperCase() + s.slice(1) : '');

// Always slice first 10 chars — handles "2026-05-06" or "2026-05-06T00:00:00Z"
export const isoOnly = (raw) => String(raw).slice(0, 10);

export const fmtDateLong = (date) =>
  titleCase(new Date(date).toLocaleDateString('es-EC', {
    weekday: 'long', day: 'numeric', month: 'long',
  }));

// Color tiers for heatmap cells based on price vs official
export const cellColor = (price) => {
  if (price == null) return '#eef0f5';
  const diff = price - PRECIO_OFICIAL;
  if (diff <= -0.50) return PALETTE.red;
  if (diff <= -0.20) return PALETTE.yellow;
  if (diff <   0.20) return '#fbcf6e';
  if (diff <   0.50) return '#79d4a3';
  return PALETTE.green;
};

// Auto-generated insight in plain Spanish for a daily series
// `data` shape: [{ value: number, ... }]
export const buildPriceInsight = (data) => {
  if (!data?.length) return null;
  const last = data[data.length - 1].value;

  let upStreak = 0;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].value >= PRECIO_OFICIAL) upStreak++;
    else break;
  }
  if (upStreak >= 3) {
    return { icon: '🌱', text: `${upStreak} días seguidos sobre el precio oficial.` };
  }

  if (last < PRECIO_OFICIAL) {
    const below = data.filter(d => d.value < PRECIO_OFICIAL).length;
    return {
      icon: '⚠️',
      text: `El precio está bajo el oficial. ${below} de los últimos ${data.length} días estuvieron debajo de ${fmtCurrency(PRECIO_OFICIAL)}.`,
    };
  }

  const above = data.filter(d => d.value >= PRECIO_OFICIAL).length;
  return {
    icon: '📊',
    text: `${above} de los últimos ${data.length} días la caja estuvo sobre el precio oficial.`,
  };
};

// "hace 5 minutos" / "hace 2 horas" — frescura humana
export const formatRelativeTime = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 30) return 'hace un momento';
  if (seconds < 60) return `hace ${seconds} segundos`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `hace ${months} ${months === 1 ? 'mes' : 'meses'}`;
  const years = Math.floor(months / 12);
  return `hace ${years} ${years === 1 ? 'año' : 'años'}`;
};

// "fresh" (<1h) / "recent" (<24h) / "stale" (<7d) / "old" / "unknown"
export const freshnessLevel = (date) => {
  if (!date) return 'unknown';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 3600)   return 'fresh';
  if (seconds < 86400)  return 'recent';
  if (seconds < 604800) return 'stale';
  return 'old';
};

// Find latest timestamp + count today's reports from raw price data
export const summarizeFreshness = (priceData) => {
  if (!priceData?.length) return { latest: null, todayCount: 0 };
  let latest = null;
  let latestTs = -Infinity;
  for (const d of priceData) {
    const t = new Date(d.date).getTime();
    if (!Number.isNaN(t) && t > latestTs) {
      latestTs = t;
      latest = d.date;
    }
  }
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = priceData.filter(d => isoOnly(d.date) === today).length;
  return { latest, todayCount };
};

// Aggregate raw price reports into a clean daily series.
// Multiple reports per day → daily average (avoids crossing polygons
// in lightweight-charts when multiple producers report on the same date).
export const aggregateDaily = (priceData, dateKey = 'date', valueKey = 'price_per_box') => {
  const buckets = new Map();
  for (const d of priceData || []) {
    const iso = isoOnly(d[dateKey]);
    const [y, m, day] = iso.split('-').map(Number);
    if (!y || !m || !day) continue;
    const v = parseFloat(d[valueKey]);
    if (Number.isNaN(v)) continue;
    if (!buckets.has(iso)) buckets.set(iso, { iso, y, m, day, values: [] });
    buckets.get(iso).values.push(v);
  }
  return [...buckets.values()]
    .sort((a, b) => a.iso.localeCompare(b.iso))
    .map(b => ({
      time: { year: b.y, month: b.m, day: b.day },
      value: b.values.reduce((s, x) => s + x, 0) / b.values.length,
      iso: b.iso,
      count: b.values.length,
    }));
};
